import React, { createContext, useContext, useMemo } from 'react'
import { PacketSection, SECTIONS, SectionKey } from './sections'

/**
 * The packet's merged deck and its section numbering (slice 10).
 *
 * Numbering used to be a module-level `sectionNumberFor` over the static
 * `SECTIONS` array. Once AEs can add slides it is per-packet, so it lives in
 * context: both hosts build the merged list once and every header, TOC row and
 * comment group reads its number from the same source.
 *
 * The provider sits at the `UnresolvedCommentsProvider` level, not inside
 * `<main>` — the All-comments drawer and `CommentThread` render outside it.
 *
 * Numbering is key-based rather than page-based so all of Creative's strategy
 * slides keep one number, and `numberFor` returns a defined value for an
 * unknown key rather than throwing: `AllCommentsDrawer` calls it for orphaned
 * anchors.
 */

/**
 * The AE's hide control (slice 12.5). Optional, and that's the point: `SectionCard`
 * already receives its `sectionKey`, so it can render the eye button itself from
 * context and no built-in card's call site has to thread a prop. Present mounts
 * the same provider without this handle, so no control ever renders there.
 */
export interface PacketHideHandle {
  hidden: Set<SectionKey>
  toggle: (key: SectionKey, hidden: boolean) => void
  pending: boolean
}

interface PacketSectionsValue {
  sections: PacketSection[]
  numberFor: (key: SectionKey) => string
  hide?: PacketHideHandle
}

const FALLBACK_NUMBER = '00'

const defaultValue: PacketSectionsValue = {
  sections: SECTIONS,
  numberFor: key => numberIn(SECTIONS, key),
}

const numberIn = (sections: PacketSection[], key: SectionKey): string => {
  const idx = sections.findIndex(s => s.key === key)
  return idx < 0 ? FALLBACK_NUMBER : String(idx + 1).padStart(2, '0')
}

const PacketSectionsContext = createContext<PacketSectionsValue>(defaultValue)

/**
 * Takes the already-merged deck rather than the raw slides: both hosts need the
 * merged list outside the provider too (the editor's `useActiveSection`,
 * Present's page list), and merging twice would hand them different identities.
 */
export const PacketSectionsProvider = ({
  sections,
  hide,
  children,
}: {
  sections: PacketSection[]
  hide?: PacketHideHandle
  children: React.ReactNode
}) => {
  const value = useMemo<PacketSectionsValue>(
    () => ({ sections, numberFor: key => numberIn(sections, key), hide }),
    [sections, hide]
  )

  return <PacketSectionsContext.Provider value={value}>{children}</PacketSectionsContext.Provider>
}

export const usePacketSections = (): PacketSectionsValue => useContext(PacketSectionsContext)

/** Convenience for the many headers that only need their own number. */
export const useSectionNumber = (key: SectionKey): string => usePacketSections().numberFor(key)
